import React, { useEffect, useState, useRef, useMemo } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { getUserList, getUser } from "../../redux/selector";
import apiServices from "../../services/service";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import AddBucketForm from "./AddBucketForm";
import Loader from "../../shared/component/Loader";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { RichSelectModule } from "@ag-grid-enterprise/rich-select";
import { ModuleRegistry } from "@ag-grid-community/core";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RangeSelectionModule,
  RowGroupingModule,
  RichSelectModule,
]);

const selectStyles = {
  container: (base) => ({ ...base, fontSize: ".8rem" }),
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#456ABB" : base.borderColor,
    boxShadow: state.isFocused ? "0 0 0 1px #456ABB" : "none",
    "&:hover": { borderColor: "#456ABB" },
  }),
  valueContainer: (base) => ({ ...base, height: "32px", padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
    color: "#222",
  }),
};

function BucketManagement() {
  const containerStyle = useMemo(() => ({ width: "100%", height: "100%" }), []);
  const bucketObjectsGridRef = useRef(null);

  // Bucket state
  const [bucketId, setBucketId] = useState();
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [dropItems, setDropItems] = useState([]);
  const [selected, setSelected] = useState();

  // Lead tab state
  const [bucketLeads, setBucketLeads] = useState([]);
  const [bucketLeadsLoading, setBucketLeadsLoading] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsToAssign, setLeadsToAssign] = useState([]);
  const [savingLeads, setSavingLeads] = useState(false);

  // Bucket Assignment tab state
  const [bucketObjects, setBucketObjects] = useState([]);
  const [bucketObjectsLoading, setBucketObjectsLoading] = useState(false);
  const [assignedToMap, setAssignedToMap] = useState({});

  // Users list sourced from Redux (fetched once in PageLayoutCRM on app mount)
  const usersList = useSelector(getUserList);
  const userInfo = useSelector(getUser);
  const userId = userInfo?.user?.userId;

  // Add Bucket modal
  const [showAddBucketModal, setShowAddBucketModal] = useState(false);

  const [activeTab, setActiveTab] = useState("Leads");

  const dropdownRef = useRef(null);

  const handleBucketSelect = (bucket) => {
    setSelected(bucket.name);
    setBucketId(bucket.id);
    setBucketLeads([]);
    setBucketObjects([]);
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsActive(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch all leads for multi-select dropdown
  useEffect(() => {
    setLeadsLoading(true);
    apiServices
      .getLeadList({ pageSize: 500, pageNumber: 1, columnName: "UpdatedDateTime", orderType: "desc", filterJson: null })
      .then((response) => setAllLeads(response.data.leads || []))
      .catch((error) => console.error("Error fetching leads:", error))
      .finally(() => setLeadsLoading(false));
  }, []);

  // Fetch buckets list (own buckets only)
  useEffect(() => {
    if (!userId) return;
    setBucketsLoading(true);
    apiServices
      .getBucketsByUserId(userId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
        setDropItems(data);
      })
      .catch((error) => console.error("Error fetching buckets:", error))
      .finally(() => setBucketsLoading(false));
  }, [userId]);

  // Fetch bucket objects when bucketId changes or user navigates to Object Assignment tab
  useEffect(() => {
    if (!bucketId || activeTab !== "Object Assignment") return;
    setBucketObjectsLoading(true);
    apiServices
      .getBucketObjects(bucketId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.bucketObjects || raw?.data || [];
        setBucketObjects(data);
      })
      .catch((error) => console.error("Error fetching bucket objects:", error))
      .finally(() => setBucketObjectsLoading(false));
  }, [bucketId, activeTab]);

  // Fetch leads for selected bucket
  useEffect(() => {
    if (!bucketId) return;
    setBucketLeadsLoading(true);
    apiServices
      .getLeadsByBucket(bucketId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.leads || raw?.data || [];
        setBucketLeads(data);
      })
      .catch((error) => console.error("Error fetching bucket leads:", error))
      .finally(() => setBucketLeadsLoading(false));
  }, [bucketId]);

  const leadOptions = useMemo(
    () =>
      allLeads.map((lead) => ({
        value: lead.id,
        label:
          `${lead.firstName || ""} ${lead.lastName || ""}`.trim() ||
          lead.fullName ||
          lead.companyName ||
          lead.id,
        meta: { company: lead.companyName, email: lead.email },
      })),
    [allLeads]
  );

  const availableLeadOptions = useMemo(() => {
    const assignedIds = new Set(bucketLeads.map((l) => l.objectId?.toLowerCase()));
    return leadOptions.filter((opt) => !assignedIds.has(opt.value?.toLowerCase()));
  }, [leadOptions, bucketLeads]);

  const handleSaveLeads = () => {
    if (!leadsToAssign.length || !bucketId) return;
    setSavingLeads(true);
    apiServices
      .addMappingToBucket({
        bucketId,
        objectType: "Lead",
        assignedToUserId: null,
        objectIds: leadsToAssign.map((l) => l.value),
      })
      .then(() => {
        toast.success("Leads assigned successfully!", { position: "top-right" });
        setLeadsToAssign([]);
        setBucketLeadsLoading(true);
        return apiServices.getLeadsByBucket(bucketId);
      })
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.leads || raw?.data || [];
        setBucketLeads(data);
      })
      .catch((error) =>
        toast.error(
          error?.response?.data?.message || error.message || "Failed to assign leads",
          { position: "top-right" }
        )
      )
      .finally(() => {
        setSavingLeads(false);
        setBucketLeadsLoading(false);
      });
  };

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 120,
  }), []);

  const bucketLeadsColDefs = useMemo(() => [
    {
      headerName: "Name",
      minWidth: 150,
      flex: 1,
      valueGetter: (p) =>
        p.data.fullName ||
        `${p.data.firstName || ""} ${p.data.lastName || ""}`.trim() ||
        "-",
    },
    {
      field: "companyName",
      headerName: "Company",
      minWidth: 150,
      flex: 1,
      valueFormatter: (p) => p.value || "-",
    },
    { field: "objectType", headerName: "Object Type", minWidth: 120, maxWidth: 150, flex: 0.6 },
    {
      field: "email",
      headerName: "Email",
      minWidth: 180,
      flex: 1.2,
      valueFormatter: (p) => p.value || "-",
    },
    {
      field: "phone",
      headerName: "Phone",
      minWidth: 130,
      flex: 0.8,
      valueGetter: (p) => p.data.phone || p.data.directPhone || p.data.mobile || "-",
    },
    {
      field: "owner",
      headerName: "Owner",
      minWidth: 130,
      flex: 0.8,
      valueFormatter: (p) => p.value || "-",
    },
  ], []);

  const bucketAssignmentColDefs = useMemo(() => [
    {
      field: "objectId",
      headerName: "Object ID",
      minWidth: 160,
      maxWidth: 260,
      flex: 1,
      tooltipField: "objectId",
      cellStyle: { fontSize: "0.78rem", color: "#666" },
    },
    { field: "objectType", headerName: "Object Type", minWidth: 120, maxWidth: 160, flex: 0.6 },
    { field: "objectName", headerName: "Object Name", minWidth: 180, flex: 1.5 },
  ], []);

  // Bucket dropdown grouped into "Public Buckets" / "Private Buckets" sections (per bucket.bucketVisibility)
  const groupedBuckets = useMemo(() => {
    const isPrivate = (b) => String(b?.bucketVisibility).toLowerCase() === "private";
    return {
      public: dropItems.filter((b) => !isPrivate(b)),
      private: dropItems.filter(isPrivate),
    };
  }, [dropItems]);

  // useEffect(() => {
  //   if (bucketObjectsGridRef.current?.api) {
  //     bucketObjectsGridRef.current.api.refreshCells({ force: true });
  //   }
  // }, []);

  const refreshBuckets = () => {
    if (!userId) return;
    setBucketsLoading(true);
    apiServices
      .getBucketsByUserId(userId)
      .then((response) => {
        const raw = response.data;
        const data = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
        setDropItems(data);
      })
      .catch((error) => console.error("Error fetching buckets:", error))
      .finally(() => setBucketsLoading(false));
  };

  const handleBucketAdded = () => {
    setShowAddBucketModal(false);
    refreshBuckets();
  };

  return (
    <>
      {bucketsLoading && <Loader />}
      <div className="inner-page" style={containerStyle}>
        <div className="dash-container">
          <div className="tbl_container">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12">
                <div className="main-hd">
                  <div>
                    <div className="pagetitle">
                      <img src="../assets/images/role-management-icons/left-arrow.png" alt="" />{" "}
                      Bucket-Management
                    </div>
                    <div className="brdText">
                      <Link to="/home" title="Home">Home</Link>{" "}
                      <span className="hs">/</span>{" "}
                      <span className="breadcrumb-without-link" title="Settings">Settings</span>
                      <span className="hs">/</span>{" "}
                      <span title="Bucket Management" className="curr">Bucket-Management</span>
                    </div>
                  </div>

                  <div style={{ position: "relative" }}>
                    {bucketsLoading && (
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 10,
                          cursor: "not-allowed",
                          backgroundColor: "transparent",
                        }}
                      />
                    )}
                    <div className="graydiv">
                      <div className="div2">
                        <div className="para">
                          <p>Bucket:&nbsp;&nbsp;</p>
                          <div className="dropdown" onClick={() => setIsActive(!isActive)} ref={dropdownRef}>
                            <div className="dropdown-btn">{selected || "Select a Bucket"}</div>
                            <span className="fas fa-caret-down"><i className="bi bi-chevron-down"></i></span>
                            {isActive && (
                              <div className="dropdown-content">
                                {dropItems.length > 0 ? (
                                  <>
                                    {groupedBuckets.public.length > 0 && (
                                      <>
                                        <div className="dropdown-group-label">
                                          Public Buckets <span className="dropdown-group-count">{groupedBuckets.public.length}</span>
                                        </div>
                                        {groupedBuckets.public.map((bucket) => (
                                          <div
                                            className="dropdown-item"
                                            key={bucket.id}
                                            onClick={() => { handleBucketSelect(bucket); setIsActive(false); }}
                                          >
                                            {bucket.name}
                                          </div>
                                        ))}
                                      </>
                                    )}
                                    {groupedBuckets.private.length > 0 && (
                                      <>
                                        <div className="dropdown-group-label">
                                          Private Buckets <span className="dropdown-group-count">{groupedBuckets.private.length}</span>
                                        </div>
                                        {groupedBuckets.private.map((bucket) => (
                                          <div
                                            className="dropdown-item"
                                            key={bucket.id}
                                            onClick={() => { handleBucketSelect(bucket); setIsActive(false); }}
                                          >
                                            {bucket.name}
                                          </div>
                                        ))}
                                      </>
                                    )}
                                  </>
                                ) : (
                                  <div className="dropdown-item" style={{ color: "#999", pointerEvents: "none" }}>
                                    No buckets — click Add Bucket
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <button className="add-btn" type="button" onClick={() => setShowAddBucketModal(true)}>
                      + Add Bucket
                    </button>
                  </div>
                </div>

                <div className="test">
                  <>
                    <div className="roleManagement-nav">
                      <ul className="nav nav-underline">
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "Leads" ? "active" : ""}`}
                            onClick={() => setActiveTab("Leads")}
                          >
                            Leads
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link ${activeTab === "Object Assignment" ? "active" : ""}`}
                            onClick={() => setActiveTab("Object Assignment")}
                          >
                            Object Assignment
                          </a>
                        </li>
                      </ul>
                    </div>

                    <div>
                      {/* Leads Tab */}
                      {activeTab === "Leads" && (
                        selected && selected.length > 0 ? (
                          <div className="parentdiv" style={{ padding: "16px", display: "flex", flexDirection: "column", height: "calc(100vh - 210px)" }}>
                            {/* Assign leads multi-select */}
                            <div style={{ marginBottom: "16px", display: "flex", alignItems: "flex-end", gap: "12px", flexShrink: 0 }}>
                              <div style={{ flex: 1 }}>
                                <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                  Assign Leads
                                </label>
                                <Select
                                  isMulti
                                  isLoading={leadsLoading}
                                  options={availableLeadOptions}
                                  value={leadsToAssign}
                                  onChange={setLeadsToAssign}
                                  placeholder="Search and select leads..."
                                  closeMenuOnSelect={false}
                                  hideSelectedOptions={false}
                                  menuPortalTarget={document.body}
                                  formatOptionLabel={(opt) => (
                                    <div style={{ fontSize: ".8rem" }}>
                                      <div style={{ fontWeight: 500 }}>{opt.label}</div>
                                      {(opt.meta?.company || opt.meta?.email) && (
                                        <div style={{ color: "#888", fontSize: ".73rem" }}>
                                          {[opt.meta.company, opt.meta.email].filter(Boolean).join(" · ")}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  styles={{
                                    container: (base) => ({ ...base, fontSize: ".8rem" }),
                                    control: (base, state) => ({
                                      ...base,
                                      borderColor: state.isFocused ? "#456ABB" : base.borderColor,
                                      boxShadow: state.isFocused ? "0 0 5px rgba(0,86,179,0.3)" : "none",
                                      "&:hover": { borderColor: "#456ABB" },
                                    }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    option: (base, state) => ({
                                      ...base,
                                      backgroundColor: state.isSelected ? "#C2DBFF" : state.isFocused ? "#f0f5ff" : "white",
                                      color: "#222",
                                    }),
                                    multiValue: (base) => ({ ...base, backgroundColor: "#dde8ff", borderRadius: "4px" }),
                                    multiValueLabel: (base) => ({ ...base, fontSize: ".75rem", color: "#275ACA" }),
                                  }}
                                />
                              </div>
                              <button
                                className="add-btn"
                                type="button"
                                onClick={handleSaveLeads}
                                disabled={savingLeads || !leadsToAssign.length}
                              >
                                {savingLeads ? "Saving..." : "Save Leads"}
                              </button>
                            </div>

                            {/* Assigned leads grid */}
                            {bucketLeadsLoading ? (
                              <div className="text-center py-4" style={{ color: "#999" }}>Loading...</div>
                            ) : (
                              <div style={{ flex: 1, minHeight: 0 }}>
                                <div className="ag-theme-balham" style={{ height: "100%", width: "100%" }}>
                                  <AgGridReact
                                    rowData={bucketLeads}
                                    columnDefs={bucketLeadsColDefs}
                                    defaultColDef={defaultColDef}
                                    rowHeight={42}
                                    animateRows
                                    suppressRowClickSelection
                                    alwaysShowVerticalScroll
                                    onGridReady={(params) => params.api.sizeColumnsToFit()}
                                    overlayNoRowsTemplate="<span style='color:#999'>No leads assigned to this bucket yet.</span>"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="role-not-selected">
                            <h1>Please select a bucket from the Bucket&apos;s dropdown</h1>
                          </div>
                        )
                      )}

                      {/* Bucket Assignment Tab */}
                      {activeTab === "Object Assignment" && (
                        selected && selected.length > 0 ? (
                          bucketObjectsLoading ? (
                            <div className="text-center py-4" style={{ color: "#999" }}>Loading...</div>
                          ) : (
                            <div
                              className="ag-theme-balham"
                              style={{ height: "calc(100vh - 210px)", width: "100%" }}
                            >
                              <AgGridReact
                                ref={bucketObjectsGridRef}
                                rowData={bucketObjects}
                                columnDefs={bucketAssignmentColDefs}
                                defaultColDef={defaultColDef}
                                rowHeight={42}
                                animateRows
                                suppressRowClickSelection
                                alwaysShowVerticalScroll
                                tooltipShowDelay={300}
                                onGridReady={(params) => params.api.sizeColumnsToFit()}
                                overlayNoRowsTemplate="<span style='color:#999'>No objects assigned to this bucket yet.</span>"
                              />
                            </div>
                          )
                        ) : (
                          <div className="role-not-selected">
                            <h1>Please select a bucket from the Bucket&apos;s dropdown</h1>
                          </div>
                        )
                      )}
                    </div>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddBucketForm
        open={showAddBucketModal}
        onClose={() => setShowAddBucketModal(false)}
        onSave={handleBucketAdded}
        usersList={usersList}
      />
    </>
  );
}

export default BucketManagement;
